import React, { useState, useMemo } from 'react';
import { useParams, Link, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Page } from '../../components';
import DhobaraPageLayout from '../../components/DhobaraPageLayout/DhobaraPageLayout';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import {
  DESIGNER_LOGOS,
  DESIGNER_DESCRIPTIONS,
  slugify,
  designerNameForSlug,
  RETIRED_DESIGNER_SLUGS,
} from '../../data/designers';
import { createSlug } from '../../util/urlHelpers';
import { isSoldOut } from '../../util/soldOut';
import css from './DesignerPage.module.css';
import {
  PRICE_MIN_DEFAULT,
  PRICE_MAX_DEFAULT,
  COLLECTION_OPTIONS,
  COLOR_OPTIONS,
  SIZE_OPTIONS,
  FABRIC_OPTIONS,
  INCLUDED_OPTIONS,
} from '../../config/dhobaraFilters';

const COLLECTION_LABEL_MAP = {
  bridal: 'Bridal',
  formals: 'Formal',
  formal: 'Formal',
  casual: 'Casual',
  mens: 'Mens',
  kids: 'Kids',
};

// ── Listing card ─────────────────────────────────────────────────────────────

function ListingCard({ listing, designerName }) {
  const attrs = listing.attributes || {};
  const title = attrs.title || '';
  const price = attrs.price;
  const pub = attrs.publicData || {};
  const collection = pub.collection;

  const priceStr = price
    ? `$${(price.amount / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`
    : null;

  const images = listing.images || [];
  const firstImage = images[0];
  const imgUrl =
    firstImage?.attributes?.variants?.['listing-card-2x']?.url ||
    firstImage?.attributes?.variants?.['listing-card']?.url ||
    firstImage?.attributes?.variants?.['scaled-medium']?.url ||
    null;

  const titleSlug = createSlug(title);
  const id = listing.id?.uuid;

  const collectionLabel = collection && COLLECTION_LABEL_MAP[collection]
    ? COLLECTION_LABEL_MAP[collection]
    : null;

  const sold = isSoldOut(listing);

  return (
    <Link to={`/l/${titleSlug}/${id}`} className={css.card}>
      <div className={css.cardImage}>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={title}
            loading="lazy"
            className={sold ? css.cardImageSold : undefined}
          />
        ) : (
          <div className={css.cardImagePlaceholder} />
        )}
        {sold && <span className={css.soldBadge}>Sold</span>}
      </div>
      {designerName && <p className={css.cardDesigner}>{designerName.toUpperCase()}</p>}
      <p className={css.cardGarment}>{title}</p>
      <div className={css.cardMeta}>
        {priceStr && <span className={css.cardPrice}>{priceStr}</span>}
        {collectionLabel && (
          <span className={css.cardCollection}>{collectionLabel.toUpperCase()}</span>
        )}
      </div>
    </Link>
  );
}

// ── Filter config ────────────────────────────────────────────────────────────
// NOTE: checkbox `value`s must match the exact publicData enum values stored on
// listings (see src/config/configListing.js) or filtering silently matches
// nothing. Labels are the shopper-facing short forms.

const FILTER_SECTIONS = [
  { key: 'collection', label: 'Collection', type: 'checkboxes', options: COLLECTION_OPTIONS },
  { key: 'price', label: 'Price Range', type: 'range', min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT, prefix: '$' },
  { key: 'color', label: 'Color', type: 'checkboxes', options: COLOR_OPTIONS },
  { key: 'size', label: 'Size', type: 'checkboxes', options: SIZE_OPTIONS },
  { key: 'fabric', label: 'Fabric', type: 'checkboxes', options: FABRIC_OPTIONS },
  { key: 'included', label: "What's Included", type: 'checkboxes', options: INCLUDED_OPTIONS },
];

// ── Icons ────────────────────────────────────────────────────────────────────

const IconFilter = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="18" height="18">
    <path d="M3 5h14M6 10h8M9 15h2" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="18" height="18">
    <path d="M4 4l12 12M16 4L4 16" />
  </svg>
);

const IconPlus = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="14" height="14">
    <path d="M8 2v12M2 8h12" />
  </svg>
);

const IconMinus = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="14" height="14">
    <path d="M2 8h12" />
  </svg>
);

// ── FilterRow (controlled) ────────────────────────────────────────────────────

const FilterRow = ({
  section,
  selectedValues,
  onToggle,
  priceMin,
  priceMax,
  onPriceMinChange,
  onPriceMaxChange,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={css.filterRow}>
      <button type="button" className={css.filterRowToggle} onClick={() => setOpen(o => !o)}>
        <span>{section.label}</span>
        {open ? <IconMinus /> : <IconPlus />}
      </button>

      {open && (
        <div className={css.filterRowBody}>
          {section.type === 'range' && (
            <div className={css.filterRangeInputs}>
              <div className={css.filterRangeField}>
                <span className={css.filterRangePrefix}>{section.prefix}</span>
                <input
                  type="number"
                  className={css.filterRangeInput}
                  value={priceMin}
                  min={section.min}
                  max={priceMax}
                  onFocus={e => e.target.select()}
                  onChange={e => onPriceMinChange(Number(e.target.value))}
                />
              </div>
              <span className={css.filterRangeSep}>–</span>
              <div className={css.filterRangeField}>
                <span className={css.filterRangePrefix}>{section.prefix}</span>
                <input
                  type="number"
                  className={css.filterRangeInput}
                  value={priceMax}
                  min={priceMin}
                  max={section.max}
                  onFocus={e => e.target.select()}
                  onChange={e => onPriceMaxChange(Number(e.target.value))}
                />
              </div>
            </div>
          )}

          {section.type === 'checkboxes' && (
            <div className={css.filterCheckboxes}>
              {section.options.map(opt => (
                <label key={opt.value} className={css.filterCheckboxLabel}>
                  <input
                    type="checkbox"
                    className={css.filterCheckbox}
                    checked={selectedValues.has(opt.value)}
                    onChange={() => onToggle(section.key, opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Empty applied-filter state ────────────────────────────────────────────────

const EMPTY_FILTERS = {
  collection: new Set(),
  price: { min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT },
  color: new Set(),
  size: new Set(),
  fabric: new Set(),
  included: new Set(),
};

// ── Main component ────────────────────────────────────────────────────────────

const DesignerPage = () => {
  const { slug } = useParams();
  const [filterOpen, setFilterOpen] = useState(false);

  // Pending filter state (inside the drawer, not yet applied)
  const [pendingPrice, setPendingPrice] = useState({ min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT });
  const [pendingCheckboxes, setPendingCheckboxes] = useState({
    collection: new Set(), color: new Set(), size: new Set(), fabric: new Set(), included: new Set(),
  });

  // Applied filter state (what actually filters the list)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const toggleCheckbox = (key, value) => {
    setPendingCheckboxes(prev => {
      const next = new Set(prev[key]);
      if (next.has(value)) next.delete(value); else next.add(value);
      return { ...prev, [key]: next };
    });
  };

  const handleApply = () => {
    setAppliedFilters({
      collection: new Set(pendingCheckboxes.collection),
      price: { ...pendingPrice },
      color: new Set(pendingCheckboxes.color),
      size: new Set(pendingCheckboxes.size),
      fabric: new Set(pendingCheckboxes.fabric),
      included: new Set(pendingCheckboxes.included),
    });
    setFilterOpen(false);
  };

  const handleClearAll = () => {
    setPendingPrice({ min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT });
    setPendingCheckboxes({
      collection: new Set(), color: new Set(), size: new Set(), fabric: new Set(), included: new Set(),
    });
    setAppliedFilters(EMPTY_FILTERS);
  };

  // Redux listings
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);
  const { inProgress, currentPageResultIds = [] } = useSelector(
    state => state.DesignerPage || {}
  );
  const allListings = useSelector(state => selectListingsById(state, currentPageResultIds));

  // Client-side filtering
  const listings = useMemo(() => {
    const { collection, price, color, size, fabric, included } = appliedFilters;
    const hasFilters =
      collection.size || color.size || size.size || fabric.size || included.size ||
      price.min > PRICE_MIN_DEFAULT || price.max < PRICE_MAX_DEFAULT;
    if (!hasFilters) return allListings;

    return allListings.filter(listing => {
      const pub = listing.attributes?.publicData || {};
      const listingPrice = (listing.attributes?.price?.amount || 0) / 100;
      if (collection.size && !collection.has(pub.collection)) return false;
      if (listingPrice < price.min || listingPrice > price.max) return false;
      if (color.size && !color.has(pub.color)) return false;
      if (size.size && !size.has(pub.size)) return false;
      if (fabric.size && !fabric.has(pub.fabric)) return false;
      if (included.size) {
        // `garmentType` is a multi-enum → stored as an array on publicData.
        const garments = Array.isArray(pub.garmentType) ? pub.garmentType : [];
        if (!Array.from(included).some(g => garments.includes(g))) return false;
      }
      return true;
    });
  }, [allListings, appliedFilters]);

  // Dhobara: accept either spelling of the slug — the canonical slugify(name)
  // the Designers page links to, and the five legacy listing keys a PDP
  // "More by …" cross-link uses (see DESIGNER_SLUG_ALIASES).
  // A retired spelling (see RETIRED_DESIGNER_SLUGS) still resolves, but sends
  // the visitor to the canonical URL rather than serving the same designer at
  // two addresses.
  const retiredTarget = RETIRED_DESIGNER_SLUGS[slug];
  if (retiredTarget) return <Redirect to={`/designer/${retiredTarget}`} />;

  const designerName = designerNameForSlug(slug);
  if (!designerName) return <Redirect to="/designers" />;

  // Everything below keys off the canonical slug, so logos, descriptions and
  // schema.org URLs resolve the same however the visitor arrived.
  const canonicalSlug = slugify(designerName);
  const logo = DESIGNER_LOGOS[canonicalSlug];
  const description = DESIGNER_DESCRIPTIONS[canonicalSlug] ||
    `Pre-loved ${designerName} pieces to make new memories with you.`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Brand',
        name: designerName,
        description: description,
        url: `https://dhobara.com/designer/${canonicalSlug}`,
      },
      {
        '@type': 'WebPage',
        name: `${designerName} | Pre-Owned Luxury on Dhobara`,
        description: `Shop authenticated pre-owned ${designerName} pieces on Dhobara. South Asian luxury resale — curated, verified, and ready to wear again.`,
        url: `https://dhobara.com/designer/${canonicalSlug}`,
        about: { '@type': 'Brand', name: designerName },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dhobara.com' },
          { '@type': 'ListItem', position: 2, name: 'Designers', item: 'https://dhobara.com/designers' },
          { '@type': 'ListItem', position: 3, name: designerName, item: `https://dhobara.com/designer/${canonicalSlug}` },
        ],
      },
    ],
  };

  return (
    <Page
      title={`${designerName} | Pre-Owned Luxury on Dhobara`}
      description={`Shop authenticated pre-owned ${designerName} pieces on Dhobara. South Asian luxury resale — curated, verified, and ready to wear again.`}
      scrollingDisabled={false}
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <DhobaraPageLayout>

        {/* HERO — logo on beige with bottom fade */}
        <section className={css.hero}>
          <div className={css.heroInner}>
            <Link to="/designers" className={css.breadcrumb}>← All Designers</Link>
            <span className={css.eyebrow}>Designer</span>
            {logo
              ? <img src={logo} alt={designerName} className={css.heroLogo} />
              : <h1 className={css.heroName}>{designerName}</h1>
            }
            {logo && <p className={css.heroSubName}>{designerName}</p>}
            <p className={css.description}>{description}</p>
          </div>
          <div className={css.heroFade} />
        </section>

        {/* LISTINGS */}
        <section className={css.body}>
          <div className={css.bodyInner}>
            <div className={css.listingBar}>
              <button type="button" className={css.filterBtn} onClick={() => setFilterOpen(true)}>
                <IconFilter />
                <span>Filter</span>
              </button>
            </div>

            {inProgress ? (
              <div className={css.loadingState}>
                <p className={css.loadingText}>Loading…</p>
              </div>
            ) : listings.length > 0 ? (
              <div className={css.grid}>
                {listings.map(listing => (
                  <ListingCard
                    key={listing.id?.uuid}
                    listing={listing}
                    designerName={designerName}
                  />
                ))}
              </div>
            ) : allListings.length === 0 ? (
              // Nobody has listed this designer at all — not a filter problem.
              <div className={css.emptyState}>
                <p className={css.emptyText}>Nothing listed yet for {designerName}.</p>
                <p className={css.emptySubText}>Be the first to list!</p>
                <Link to="/dashboard" className={css.emptyBtn}>Submit a Listing</Link>
              </div>
            ) : (
              // Listings exist, but the applied filters excluded all of them.
              <div className={css.emptyState}>
                <p className={css.emptyText}>No listings match these filters for {designerName}.</p>
                <p className={css.emptySubText}>
                  Try clearing some filters, or check back soon.
                </p>
                <Link to="/dashboard" className={css.emptyBtn}>Submit a Listing</Link>
              </div>
            )}
          </div>
        </section>

        {/* FILTER DRAWER */}
        {filterOpen && <div className={css.drawerBackdrop} onClick={() => setFilterOpen(false)} />}
        <div className={filterOpen ? `${css.drawer} ${css.drawerOpen}` : css.drawer}>
          <div className={css.drawerHeader}>
            <span className={css.drawerTitle}>Filter</span>
            <button type="button" className={css.drawerClose} onClick={() => setFilterOpen(false)}>
              <IconClose />
            </button>
          </div>
          <div className={css.drawerBody}>
            {FILTER_SECTIONS.map(s => (
              <FilterRow
                key={s.key}
                section={s}
                selectedValues={pendingCheckboxes[s.key] || new Set()}
                onToggle={toggleCheckbox}
                priceMin={pendingPrice.min}
                priceMax={pendingPrice.max}
                onPriceMinChange={min => setPendingPrice(p => ({ ...p, min }))}
                onPriceMaxChange={max => setPendingPrice(p => ({ ...p, max }))}
              />
            ))}
          </div>
          <div className={css.drawerFooter}>
            <button type="button" className={css.drawerClear} onClick={handleClearAll}>Clear All</button>
            <button type="button" className={css.drawerApply} onClick={handleApply}>Apply</button>
          </div>
        </div>


      </DhobaraPageLayout>
    </Page>
  );
};

export default DesignerPage;
